import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Mail, Lock, ArrowRight, AlertCircle, CheckCircle2, Loader2, X } from "lucide-react";
import { businessLogin } from "../ApiCalls/authentication";
import { useNavigate } from "react-router-dom";
import { brand } from "../config";
import mantisLogo from "../Assets/img/mantisBlackLogo.png";
import sandyLogo from "../Assets/img/sandyBlackLogo.png";
import { connect } from "react-redux";
import {
  userLogin,
  setUser,
  setToken,
  setUserId,
  setIsBusiness,
  setMantisBusiness,
} from "../Redux/modules/auth";
import { sendRecoverPasswordEmail } from "../ApiCalls/mantisBusiness";

function mapDispatchToProps(dispatch) {
  return {
    userLogin: () => dispatch(userLogin()),
    setUser: (user) => dispatch(setUser(user)),
    setUserId: (userId) => dispatch(setUserId(userId)),
    setToken: (token) => dispatch(setToken(token)),
    setIsBusiness: (isBusiness) => dispatch(setIsBusiness(isBusiness)),
    setMantisBusiness: (mantisBusiness) =>
      dispatch(setMantisBusiness(mantisBusiness)),
  };
}

const select = (state) => {
  return {
    user: state.auth.user,
  };
};

function BusinessLogin(props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [showSessionExpiredToast, setShowSessionExpiredToast] = useState(false);
  const [showLoginRequiredToast, setShowLoginRequiredToast] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [loginRequiredToastVisible, setLoginRequiredToastVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  function showToast() {
    setShowSessionExpiredToast(true);
    setToastVisible(true);

    setTimeout(() => {
      setToastVisible(false);
      setTimeout(() => {
        setShowSessionExpiredToast(false);
      }, 500);
    }, 3000);
  }

  function handleShowLoginRequiredToast() {
    setShowLoginRequiredToast(true);
    setLoginRequiredToastVisible(true);

    setTimeout(() => {
      setLoginRequiredToastVisible(false);
      setTimeout(() => {
        setShowLoginRequiredToast(false);
      }, 500);
    }, 4000);
  }

  useEffect(() => {
    const sessionExpired = localStorage.getItem("sessionExpired");
    const loginRequired = localStorage.getItem("mantis_login_required");

    if (sessionExpired) {
      showToast();
      localStorage.removeItem("sessionExpired");
    }

    if (loginRequired) {
      handleShowLoginRequiredToast();
      localStorage.removeItem("mantis_login_required");
    }
  }, []);

  async function submitLogin(event) {
    if (!loggingIn) {
      event.preventDefault();
      event.stopPropagation();
      setLoggingIn(true);
      const loginRes = await businessLogin(email, password);
      if (loginRes.success) {
        // L-01 (MA-06): the backend now sets the mantis_session (httpOnly) and
        // csrfToken cookies itself via Set-Cookie — nothing to persist here.
        await updateUserSession(
          loginRes.userId,
          loginRes.user,
          loginRes.token,
          loginRes.mantisBusiness
        );
      } else {
        alert("Credenciales incorrectas");
      }
      setLoggingIn(false);
    }
  }

  function openForgotPasswordModal() {
    setForgotPasswordEmail("");
    setForgotPasswordLoading(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordError("");
    setShowForgotPasswordModal(true);
  }

  function closeForgotPasswordModal() {
    setShowForgotPasswordModal(false);
    setForgotPasswordEmail("");
    setForgotPasswordLoading(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordError("");
  }

  async function handleSendRecoverPasswordMail(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!forgotPasswordEmail || forgotPasswordLoading) return;
    setForgotPasswordLoading(true);
    setForgotPasswordError("");

    try {
      const res = await sendRecoverPasswordEmail(forgotPasswordEmail.trim().toLowerCase());
      if (res && res.success) {
        setForgotPasswordSuccess(true);
      } else {
        setForgotPasswordError(res?.message || "No pudimos procesar la solicitud. Intenta de nuevo.");
      }
    } catch (err) {
      console.error("Error al enviar correo de recuperación:", err);
      setForgotPasswordError("Error de conexión al servidor. Inténtalo más tarde.");
    } finally {
      setForgotPasswordLoading(false);
    }
  }

  function updateUserSession(userId, user, token, mantisBusiness) {
    props.setUser({ user });
    props.setToken({ token });
    props.setUserId({ userId });
    props.setMantisBusiness({ mantisBusiness });
    props.userLogin();
    props.setIsBusiness({ isBusiness: true });
    
    // Verificar si hay una URL de redirección guardada
    const redirectUrl = localStorage.getItem('mantis_redirect_url');
    if (redirectUrl) {
      localStorage.removeItem('mantis_redirect_url');
      navigate(redirectUrl);
    } else {
      navigate("/business/backoffice");
    }
  }

  return (
    <>
      {/* Custom Keyframe Animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes cardEntry {
            0% {
              opacity: 0;
              transform: translateY(24px) scale(0.98);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          @keyframes floatOrb1 {
            0%, 100% {
              transform: translate(0px, 0px) scale(1);
            }
            50% {
              transform: translate(25px, -20px) scale(1.08);
            }
          }
          @keyframes floatOrb2 {
            0%, 100% {
              transform: translate(0px, 0px) scale(1);
            }
            50% {
              transform: translate(-20px, 25px) scale(0.95);
            }
          }
          @keyframes subtleFloat {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-6px);
            }
          }
          @keyframes shimmerGlow {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }
          .animate-card-entry {
            animation: cardEntry 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          .animate-float-1 {
            animation: floatOrb1 10s ease-in-out infinite;
          }
          .animate-float-2 {
            animation: floatOrb2 12s ease-in-out infinite;
          }
          .animate-subtle-float {
            animation: subtleFloat 5s ease-in-out infinite;
          }
          .btn-glow-shimmer {
            background-size: 200% 100%;
            transition: all 0.3s ease;
          }
          .btn-glow-shimmer:hover {
            background-position: right center;
          }
        `
      }} />
      
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden">
        {/* Subtle Global Background Ambient Lights */}
        <div className="absolute top-[-10%] left-[-10%] w-[45vw] h-[45vw] rounded-full bg-gradient-to-br from-orange-200/20 to-transparent blur-3xl pointer-events-none animate-float-1"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[45vw] h-[45vw] rounded-full bg-gradient-to-tl from-amber-200/20 to-transparent blur-3xl pointer-events-none animate-float-2"></div>

        {/* Unified Master Card with Elegant Entry Animation */}
        <div className="relative w-full max-w-5xl bg-white rounded-[32px] shadow-[0_25px_70px_-15px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.04)] border border-slate-200/80 overflow-hidden grid lg:grid-cols-2 min-h-[580px] z-10 animate-card-entry">
          
          {/* Left Column - Distinct, Spacious, Centered Branding with Living Ambient Animation */}
          <div className="bg-gradient-to-br from-orange-100/70 via-amber-50/60 to-orange-50/80 p-10 sm:p-14 lg:p-16 flex flex-col justify-center items-center text-center relative overflow-hidden border-b lg:border-b-0 lg:border-r-2 border-orange-200/70">
            {/* Animated Ambient Light Blobs */}
            <div className="absolute -top-16 -left-16 w-80 h-80 bg-gradient-to-br from-orange-300/30 to-amber-200/20 rounded-full blur-3xl pointer-events-none animate-float-1"></div>
            <div className="absolute -bottom-16 -right-16 w-80 h-80 bg-gradient-to-tl from-orange-400/20 to-amber-300/25 rounded-full blur-3xl pointer-events-none animate-float-2"></div>

            <div className="relative z-10 max-w-md mx-auto space-y-6 flex flex-col items-center">
              {/* Large Mantis Logo - Completely Static */}
              <div className="flex justify-center">
                <img
                  src={brand === "MANTIS" ? mantisLogo : sandyLogo}
                  alt="Logo Mantis"
                  className="h-20 sm:h-24 md:h-28 w-auto object-contain drop-shadow-sm"
                />
              </div>

              {/* Centered Title */}
              <div className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-800 tracking-tight">
                  Bienvenido
                </h2>
              </div>
            </div>
          </div>

          {/* Right Column - Clean Centered Login Form */}
          <div className="p-10 sm:p-14 lg:p-16 flex flex-col justify-center bg-white">
            <div className="max-w-md w-full mx-auto space-y-7">
              {/* Centered Form Header */}
              <div className="text-center space-y-2">
                <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
                  Iniciar Sesión
                </h1>
                <p className="text-sm text-slate-500">
                  Ingresa tus credenciales para acceder a tu cuenta.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={submitLogin} className="space-y-5">
                {/* Email Field */}
                <div className="space-y-1.5 text-left">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                    Correo Electrónico
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <Mail className="h-4 w-4" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="usuario@empresa.com"
                      className="w-full pl-10 pr-4 py-3.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-orange-500 rounded-xl text-slate-900 placeholder-slate-400 text-sm font-medium focus:ring-4 focus:ring-orange-500/10 transition-all outline-none shadow-sm"
                      disabled={loggingIn}
                      required
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-1.5 text-left">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                    Contraseña
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <Lock className="h-4 w-4" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-11 py-3.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-orange-500 rounded-xl text-slate-900 placeholder-slate-400 text-sm font-medium focus:ring-4 focus:ring-orange-500/10 transition-all outline-none shadow-sm"
                      disabled={loggingIn}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                      disabled={loggingIn}
                      aria-label="Alternar visibilidad de contraseña"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Forgot Password Link - Refined & Aligned */}
                <div className="flex justify-end pt-0.5">
                  <button
                    type="button"
                    onClick={openForgotPasswordModal}
                    className="text-sm font-semibold text-orange-600 hover:text-orange-700 hover:underline transition-colors cursor-pointer"
                    disabled={loggingIn}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  disabled={loggingIn || !email || !password}
                  className="w-full py-3.5 px-6 bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-bold text-sm shadow-md shadow-orange-500/25 hover:shadow-lg hover:shadow-orange-500/35 transition-all duration-200 flex items-center justify-center gap-2 transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed mt-2 btn-glow-shimmer"
                >
                  {loggingIn ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span>Ingresar al Sistema</span>
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>

                {/* Register Link - Polished & Centered */}
                <div className="text-center pt-3 border-t border-slate-100 mt-2">
                  <p className="text-xs sm:text-sm text-slate-600 inline-flex items-center justify-center flex-wrap gap-1.5 mx-auto text-center">
                    <span>¿No tienes una cuenta corporativa?</span>
                    <button
                      type="button"
                      onClick={() => navigate("/businessRegister")}
                      className="text-orange-600 hover:text-orange-700 font-bold hover:underline transition-colors cursor-pointer"
                      disabled={loggingIn}
                    >
                      Regístrate aquí
                    </button>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Gorgeous Enterprise-Grade Forgot Password Modal */}
        {showForgotPasswordModal && (
          <div className="fixed inset-0 bg-slate-950/65 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
            <div className="bg-white/95 backdrop-blur-2xl rounded-[28px] shadow-[0_25px_70px_-15px_rgba(249,115,22,0.25),0_0_0_1px_rgba(249,115,22,0.12)] max-w-md w-full p-8 sm:p-9 relative overflow-hidden border border-orange-100/90 animate-in zoom-in-95 duration-200">
              
              {/* Top Accent Gradient Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-600 via-amber-500 to-orange-400"></div>

              {/* Ambient Background Glows */}
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-orange-400/20 to-amber-300/15 rounded-full blur-3xl pointer-events-none"></div>
              <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-gradient-to-tr from-orange-500/15 to-amber-400/10 rounded-full blur-3xl pointer-events-none"></div>

              {/* Close Button */}
              <button
                type="button"
                onClick={closeForgotPasswordModal}
                disabled={forgotPasswordLoading}
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-all duration-200 z-10"
                aria-label="Cerrar modal"
              >
                <X className="h-5 w-5" />
              </button>

              {!forgotPasswordSuccess ? (
                <div className="space-y-6 relative z-10">
                  {/* Header */}
                  <div className="text-center space-y-2 pt-1">
                    <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                      Recuperar Contraseña
                    </h2>
                    <p className="text-sm text-slate-500 text-center leading-relaxed max-w-sm mx-auto">
                      Ingresa tu correo y te enviaremos un enlace seguro para restablecer tu contraseña.
                    </p>
                  </div>

                  {/* Error Banner */}
                  {forgotPasswordError && (
                    <div className="bg-red-50/90 border border-red-200 rounded-2xl p-3.5 flex items-start space-x-2.5 text-red-700 text-xs shadow-sm animate-in fade-in duration-200">
                      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-red-500" />
                      <div className="flex-1">
                        <p className="font-semibold text-red-800">No se pudo enviar el enlace</p>
                        <p className="text-red-600 mt-0.5">{forgotPasswordError}</p>
                      </div>
                    </div>
                  )}

                  {/* Form */}
                  <form onSubmit={handleSendRecoverPasswordMail} className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                        Correo Electrónico Empresarial
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 group-focus-within:text-orange-500 transition-colors">
                          <Mail className="h-5 w-5" />
                        </div>
                        <input
                          type="email"
                          value={forgotPasswordEmail}
                          onChange={(e) => setForgotPasswordEmail(e.target.value)}
                          placeholder="tu.nombre@empresa.com"
                          className="w-full pl-11 pr-4 py-3.5 bg-slate-50/80 hover:bg-white focus:bg-white border border-slate-200 focus:border-orange-500 rounded-xl text-slate-900 placeholder-slate-400 text-sm font-medium focus:ring-4 focus:ring-orange-500/10 transition-all outline-none shadow-sm"
                          required
                          disabled={forgotPasswordLoading}
                          autoFocus
                        />
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 pt-1">
                        <Lock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                        <span>El enlace expira en 60 minutos por políticas de seguridad</span>
                      </div>
                    </div>

                    {/* Action Buttons - Perfectly balanced */}
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <button
                        type="button"
                        onClick={closeForgotPasswordModal}
                        disabled={forgotPasswordLoading}
                        className="w-full py-3.5 px-4 text-slate-700 bg-slate-100 hover:bg-slate-200/80 rounded-xl font-semibold text-sm transition-all duration-200 text-center whitespace-nowrap border border-slate-200/60"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={forgotPasswordLoading || !forgotPasswordEmail}
                        className="w-full py-3.5 px-4 bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-bold text-sm transition-all duration-200 shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/35 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-center whitespace-nowrap"
                      >
                        {forgotPasswordLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Enviando...</span>
                          </>
                        ) : (
                          <>
                            <span>Enviar Enlace</span>
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                /* Human, High-End SaaS Success View */
                <div className="space-y-6 text-center py-3 relative z-10 animate-in fade-in zoom-in-95 duration-200">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200/80 shadow-sm mx-auto">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    <span>ENLACE ENVIADO</span>
                  </div>

                  {/* Clean Typography */}
                  <div className="space-y-2 max-w-sm mx-auto">
                    <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                      Revisa tu correo
                    </h2>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Enviamos un enlace para restablecer tu contraseña a:
                    </p>
                    <p className="text-sm font-semibold text-slate-900 bg-slate-50 py-1.5 px-3 rounded-lg border border-slate-200/80 inline-block font-mono">
                      {forgotPasswordEmail}
                    </p>
                  </div>

                  <p className="text-xs text-slate-400 max-w-xs mx-auto leading-normal">
                    Si no lo encuentras en unos minutos, revisa tu carpeta de spam o correo no deseado.
                  </p>

                  {/* Actions */}
                  <div className="space-y-3 pt-2">
                    <button
                      type="button"
                      onClick={closeForgotPasswordModal}
                      className="w-full py-3.5 px-5 bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-xl font-bold text-sm transition-all duration-200 shadow-md shadow-orange-500/20 hover:shadow-lg"
                    >
                      Volver a Iniciar Sesión
                    </button>

                    <button
                      type="button"
                      onClick={handleSendRecoverPasswordMail}
                      disabled={forgotPasswordLoading}
                      className="text-xs text-orange-600 hover:text-orange-700 font-semibold transition-colors hover:underline"
                    >
                      {forgotPasswordLoading ? "Reenviando..." : "¿No recibiste el correo? Reenviar"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Toast Notifications */}
        {showSessionExpiredToast && (
          <div className={`fixed top-4 right-4 bg-white border-l-4 border-red-500 rounded shadow-lg p-4 max-w-md z-50 transition-all duration-300 ${toastVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900">Sesión Expirada</p>
                <p className="text-sm text-slate-600">Tu sesión ha expirado. Por favor, inicia sesión nuevamente.</p>
              </div>
            </div>
          </div>
        )}

        {showLoginRequiredToast && (
          <div className={`fixed top-4 right-4 bg-white border-l-4 border-orange-500 rounded shadow-lg p-4 max-w-md z-50 transition-all duration-300 ${loginRequiredToastVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900">Login Requerido</p>
                <p className="text-sm text-slate-600">🔐 Necesitas iniciar sesión para acceder a esa página. Después del login serás redirigido automáticamente.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default connect(select, mapDispatchToProps)(BusinessLogin);